import { useState } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useRomanNumbers, type Difficulty } from './useRomanNumbers'
import { useSessionMode } from '../../professional/SessionModeContext'
import { DIFFICULTY_CHOICES } from './romanNumbersLogic'
import GameSetupScreen from '../GameSetupScreen'
import GameCompleteScreen from '../GameCompleteScreen'
import GamePlayHeader from '../GamePlayHeader'
import SaveErrorBanner from '../SaveErrorBanner'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useTimer } from '../useTimer'
import { formatGameTime } from '../utils'
import { COLORS, SHADOWS, RADIUS } from '../../styles/tokens'
import RomanDisplay from './RomanDisplay'

type GameScreen = 'setup' | 'playing' | 'complete'

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

/* ── Root ──────────────────────────────────────────────────────── */
export default function RomanNumbersGame({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation()
  const sessionMode = useSessionMode()
  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : 'setup')
  const [difficulty, setDifficulty] = useState<Difficulty>(
    (sessionMode?.config.difficulty as Difficulty) ?? 'easy',
  )

  const { state, resetGame, selectOption, setInputValue, submitInput } =
    useRomanNumbers(difficulty)

  const { gameComplete, moves, startTime, endTime } = state

  const { sessionId } = useGameSession({
    activitySlug: 'roman',
    difficulty,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'roman',
    difficulty,
    moves,
    startTime,
    endTime,
    pairsTotal:   state.questions.length,
    pairsMatched: state.answered,
    sessionId,
    onCompleted:  () => setScreen('complete'),
  })

  const handleStart = () => {
    resetGame()
    setScreen('playing')
  }

  return (
    <div style={s.root}>
      {saveError && (
        <SaveErrorBanner
          message={t.common.saveError}
          dismissLabel={t.common.dismissError}
          onDismiss={clearSaveError}
        />
      )}
      {screen === 'setup' && (
        <SetupScreen
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={state}
          difficulty={difficulty}
          onSelectOption={selectOption}
          onSetInputValue={setInputValue}
          onSubmitInput={submitInput}
          onBack={() => setScreen('setup')}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🏛️"
          title={t.roman.complete.title}
          subtitle={t.roman.complete.subtitle}
          stats={[
            { value: String(state.moves),                               label: t.roman.complete.movesLabel },
            { value: formatGameTime(state.startTime, state.endTime),    label: t.roman.complete.timeLabel },
            { value: t.roman.difficulty[difficulty].label,              label: t.roman.complete.levelLabel },
          ]}
          playAgainLabel={t.roman.complete.playAgain}
          changeLevelLabel={t.roman.complete.changeLevel}
          backLabel={t.roman.complete.backToDashboard}
          onPlayAgain={() => { resetGame(); setScreen('playing') }}
          onChangeLevel={() => setScreen('setup')}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ──────────────────────────────────────────────── */
function SetupScreen({ difficulty, onDifficultyChange, onStart, onBack }: {
  difficulty:          Difficulty
  onDifficultyChange:  (d: Difficulty) => void
  onStart:             () => void
  onBack:              () => void
}) {
  const { t } = useTranslation()
  const ro = t.roman

  const preview = (
    <div style={s.previewCard}>
      <div style={s.previewLabel}>{ro.previewLabel}</div>
      <div style={s.previewNumber}>14</div>
      <div style={s.previewOptionsRow}>
        {['XII', 'XIV', 'XVI', 'XL'].map((opt, i) => (
          <div key={opt} style={{ ...s.previewOption, ...(i === 1 ? s.previewOptionCorrect : {}) }}>
            {opt}
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <GameSetupScreen
      title={ro.title}
      subtitle={ro.subtitle}
      backLabel={ro.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...ro.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={ro.chooseDifficultyTitle}
      startLabel={ro.startBtn}
      onStart={onStart}
      preview={preview}
    />
  )
}

/* ── Play Screen ───────────────────────────────────────────────── */
function PlayScreen({ state, difficulty, onSelectOption, onSetInputValue, onSubmitInput, onBack }: {
  state:            ReturnType<typeof useRomanNumbers>['state']
  difficulty:       Difficulty
  onSelectOption:   (arabic: number) => void
  onSetInputValue:  (val: string) => void
  onSubmitInput:    () => void
  onBack:           () => void
}) {
  const { t } = useTranslation()
  const ro      = t.roman
  const elapsed = useTimer(state.startTime, state.endTime)
  const total   = state.questions.length
  const done    = state.answered
  const current = state.questions[state.currentIdx]
  const isHard  = difficulty === 'hard'

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && state.inputValue.trim()) onSubmitInput()
  }

  if (!current) return null

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={ro.title}
        stats={[
          { value: state.moves, label: ro.moves },
          { value: elapsed,     label: ro.time },
        ]}
        progress={(done / total) * 100}
        progressNow={done}
        progressMax={total}
        onBack={onBack}
        backLabel={ro.backToDashboard}
      />

      <main style={s.quizMain}>
        {/* Question counter */}
        <div style={s.counterPill}>
          {ro.questionCounter
            .replace('{n}', String(done + 1))
            .replace('{total}', String(total))}
        </div>

        {/* Question card — arabic number (MCQ) or roman numeral (hard) */}
        <div style={s.questionCard}>
          <div style={s.questionLabel}>
            {isHard ? ro.questionPromptHard : ro.questionPrompt}
          </div>
          {isHard
            ? <div style={s.questionRoman}><RomanDisplay roman={current.roman} /></div>
            : <div style={s.questionNumber}>{current.arabic}</div>
          }
        </div>

        {/* MCQ options (easy / medium) */}
        {!isHard && (
          <div style={{
            ...s.optionsGrid,
            gridTemplateColumns: DIFFICULTY_CHOICES[difficulty] === 4 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          }}>
            {state.options.map((option) => {
              const isEliminated = state.eliminated.has(option.arabic)
              const isFlash      = state.correctFlashId === option.arabic
              return (
                <button
                  key={option.arabic}
                  onClick={() => onSelectOption(option.arabic)}
                  disabled={isEliminated || state.correctFlashId !== null}
                  aria-label={`${option.roman.replace(/\[|\]/g, '')}${isEliminated ? ' (incorrect)' : ''}${isFlash ? ' (correct)' : ''}`}
                  aria-pressed={isFlash ? true : undefined}
                  style={{
                    ...s.optionBtn,
                    ...(isEliminated ? s.optionBtnWrong   : {}),
                    ...(isFlash      ? s.optionBtnCorrect : {}),
                  }}
                >
                  {isEliminated && <span style={s.optionMark} aria-hidden>✗</span>}
                  {isFlash      && <span style={s.optionMark} aria-hidden>✓</span>}
                  <RomanDisplay roman={option.roman} style={s.optionRoman} />
                </button>
              )
            })}
          </div>
        )}

        {/* Numeric input (hard) — user sees roman, types arabic */}
        {isHard && (
          <div style={s.hardArea}>
            <div style={s.inputRow}>
              <input
                type="tel"
                inputMode="numeric"
                value={state.inputValue}
                onChange={(e) => onSetInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={ro.typeAnswer}
                disabled={state.correctFlashId !== null}
                style={{
                  ...s.textInput,
                  ...(state.inputStatus === 'wrong'   ? s.textInputWrong   : {}),
                  ...(state.inputStatus === 'correct' ? s.textInputCorrect : {}),
                }}
                autoComplete="off"
              />
              <button
                onClick={onSubmitInput}
                disabled={!state.inputValue.trim() || state.correctFlashId !== null}
                style={s.submitBtn}
              >
                {ro.submitBtn}
              </button>
            </div>
            {state.inputStatus === 'wrong' && (
              <p style={s.wrongFeedback}>{ro.wrongFeedback}</p>
            )}
            <p style={s.hardHint}>{ro.hardHint}</p>
          </div>
        )}
      </main>
    </div>
  )
}

/* ── Styles ────────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh' },

  // Setup preview
  previewCard: {
    background:     COLORS.bgCard,
    border:         `1.5px solid ${COLORS.borderWarm}`,
    borderRadius:   RADIUS.xxl,
    padding:        '24px 20px',
    width:          '100%',
    boxShadow:      SHADOWS.medium,
    display:        'flex',
    flexDirection:  'column',
    gap:            12,
  },
  previewLabel: {
    fontSize: '0.875rem',
    fontWeight:    800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color:         COLORS.textSecondary,
  },
  previewNumber: {
    fontSize:   '3rem',
    fontWeight: 700,
    color:      COLORS.greenDark,
    textAlign:  'center',
    padding:    '12px 0',
  },
  previewOptionsRow: {
    display:             'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap:                 8,
  },
  previewOption: {
    fontSize:     '1rem',
    fontWeight:   700,
    color:        COLORS.textDark,
    padding:      '10px 12px',
    background:   COLORS.bgPage,
    borderRadius: RADIUS.sm,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    textAlign:    'center',
  },
  previewOptionCorrect: {
    background: COLORS.gradientPrimary,
    border:     `1.5px solid ${COLORS.green}`,
    color:      COLORS.bgCard,
  },

  // Play screen
  playRoot: {
    minHeight:     '100vh',
    display:       'flex',
    flexDirection: 'column',
    background:    COLORS.bgPage,
  },
  quizMain: {
    flex:          1,
    maxWidth:      600,
    width:         '100%',
    margin:        '0 auto',
    padding:       '32px 20px 48px',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           24,
  },
  counterPill: {
    fontSize: '0.875rem',
    fontWeight:    700,
    color:         COLORS.greenDark,
    background:    COLORS.greenSurface,
    border:        `1.5px solid ${COLORS.greenPale}`,
    borderRadius:  20,
    padding:       '6px 16px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  questionCard: {
    width:          '100%',
    background:     COLORS.bgCard,
    border:         `1.5px solid ${COLORS.borderWarm}`,
    borderRadius:   RADIUS.xl,
    padding:        '32px 28px',
    boxShadow:      SHADOWS.medium,
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            8,
    textAlign:      'center',
  },
  questionLabel: {
    fontSize: '0.875rem',
    fontWeight:    800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color:         COLORS.textSecondary,
  },
  questionNumber: {
    fontSize:   'clamp(3rem, 10vw, 5rem)',
    fontWeight: 700,
    color:      COLORS.greenDark,
    lineHeight: 1,
  },
  questionRoman: {
    fontSize:      'clamp(2rem, 8vw, 3.5rem)',
    fontWeight:    700,
    color:         COLORS.greenDark,
    lineHeight:    1.2,
    letterSpacing: '0.08em',
    wordBreak:     'break-all',
  },
  optionsGrid: {
    display: 'grid',
    gap:     12,
    width:   '100%',
  },
  optionBtn: {
    position:       'relative',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            6,
    width:          '100%',
    minHeight:      64,
    padding:        '14px 12px',
    background:     COLORS.bgCard,
    border:         `1.5px solid ${COLORS.borderWarm}`,
    borderRadius:   RADIUS.card,
    cursor:         'pointer',
    boxShadow:      SHADOWS.subtle,
    transition:     'all 0.15s ease',
  },
  optionBtnWrong: {
    background: '#B5541A',
    border:     '2px solid #A04010',
    color:      COLORS.bgCard,
    cursor:     'default',
    boxShadow:  '0 2px 12px rgba(181,84,26,0.25)',
  },
  optionBtnCorrect: {
    background: COLORS.gradientPrimary,
    border:     `2px solid ${COLORS.green}`,
    color:      COLORS.bgCard,
    boxShadow:  SHADOWS.active,
  },
  optionMark: {
    fontSize:   '0.9rem',
    fontWeight: 800,
    opacity:    0.85,
  },
  optionRoman: {
    fontSize:      '1.3rem',
    fontWeight:    700,
    letterSpacing: '0.05em',
  },

  // Hard mode
  hardArea: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'stretch',
    gap:           12,
    width:         '100%',
  },
  inputRow: {
    display: 'flex',
    gap:     10,
    width:   '100%',
  },
  textInput: {
    flex:          1,
    fontSize:      '1.2rem',
    fontWeight:    700,
    color:         COLORS.textDark,
    background:    COLORS.bgCard,
    border:        `2px solid ${COLORS.borderWarm}`,
    borderRadius:  RADIUS.md,
    padding:       '14px 16px',
    outline:       'none',
    minHeight:     56,
    letterSpacing: '0.05em',
    transition:    'border-color 0.2s ease',
  },
  textInputWrong: {
    borderColor: '#B5541A',
    background:  '#FFF5F0',
  },
  textInputCorrect: {
    borderColor: COLORS.greenLight,
    background:  COLORS.greenSurface,
  },
  submitBtn: {
    fontFamily:    'Nunito, sans-serif',
    fontSize:      '1rem',
    fontWeight:    800,
    color:         COLORS.bgCard,
    background:    COLORS.gradientPrimary,
    border:        'none',
    borderRadius:  RADIUS.md,
    padding:       '14px 22px',
    cursor:        'pointer',
    minHeight:     56,
    letterSpacing: '0.02em',
    whiteSpace:    'nowrap',
    transition:    'opacity 0.15s ease',
  },
  wrongFeedback: {
    fontSize:   '0.95rem',
    fontWeight: 700,
    color:      '#B5541A',
    textAlign:  'center',
    margin:     0,
  },
  hardHint: {
    fontSize: '0.875rem',
    color:     COLORS.textSecondary,
    textAlign: 'center',
    margin:    0,
    fontStyle: 'italic',
  },
}
